/**
 * Pure helpers for the Meta frontend flows (#5742 H9).
 *
 * The OAuth-callback classification, wizard search validation, and account-picker auto-open decision
 * used to live inline in their components (a `useEffect`, a route `validateSearch`, another `useEffect`),
 * which made them untestable without a DOM. Lifted here as pure functions so they carry unit tests
 * (the frontend has no component-render harness — only `bun test` over pure logic).
 */

import type { MetaCampaignType, MetaIntegrationStatus } from '@/lib/types';

// --- OAuth callback -------------------------------------------------------------------------------

/** What the Meta OAuth callback route should do, derived purely from the redirect's query string. */
export type MetaCallbackOutcome =
  | { kind: 'error'; message: string } // Meta returned ?error= (user cancelled / denied)
  | { kind: 'success'; code: string; state: string } // code + state present → exchange them
  | { kind: 'invalid'; message: string }; // neither error nor a usable code+state pair

/**
 * Classify a Meta OAuth callback query string into the action the callback route must take.
 *
 * ``?error=`` wins (surface Meta's `error_description`); otherwise a `code` + `state` pair is a success;
 * anything else (a code without state, an empty callback) is invalid. Mirrors the branching that lived
 * in the `MetaCallback` effect so the behaviour is identical but now testable.
 */
export function classifyMetaCallback(search: string): MetaCallbackOutcome {
  const params = new URLSearchParams(search);

  const error = params.get('error');
  if (error) {
    return {
      kind: 'error',
      message: params.get('error_description') || 'Meta authorization was cancelled or failed',
    };
  }

  const code = params.get('code');
  const state = params.get('state');
  if (code && state) {
    return { kind: 'success', code, state };
  }

  return { kind: 'invalid', message: 'Missing required parameters from the Meta callback' };
}

// --- Wizard entry ---------------------------------------------------------------------------------

/** The campaign phases the Meta creation wizard accepts as its required `phase` search param. */
export const META_WIZARD_PHASES: readonly MetaCampaignType[] = ['SCALING', 'TESTING', 'RETARGETING'] as const;

/** Default phase when the search param is missing or not a recognised phase. */
export const META_WIZARD_DEFAULT_PHASE: MetaCampaignType = 'TESTING';

/**
 * Max ad creatives per phase-campaign. Mirrors the backend `MetaCreativeService.MAX_CREATIVES`; the
 * router rejects a larger `ads` list with a 422, so the wizard caps selection at this to keep the
 * user from hitting that server-side error (#5742 M-1).
 */
export const META_MAX_CREATIVES = 8;

/**
 * Toggle an asset id in the wizard's creative selection under the {@link META_MAX_CREATIVES} cap: an
 * already-selected id is removed; a new id is added only while under the cap, otherwise the selection
 * is returned unchanged (so a click past the cap is a no-op, not a server-side 422) (#5742 M-1).
 */
export function toggleCreativeSelection(selected: number[], id: number, max: number = META_MAX_CREATIVES): number[] {
  if (selected.includes(id)) return selected.filter((existing) => existing !== id);
  if (selected.length >= max) return selected;
  return [...selected, id];
}

/**
 * Validate + normalise the Meta wizard's search params. ``phase`` falls back to
 * :data:`META_WIZARD_DEFAULT_PHASE` when absent/invalid; ``offer_url`` is carried through only when a
 * string. Mirrors the route's ``validateSearch`` so the entry point stays a one-liner over this.
 */
export function parseMetaWizardSearch(search: Record<string, unknown>): {
  phase: MetaCampaignType;
  offer_url?: string;
} {
  const phase =
    typeof search.phase === 'string' && (META_WIZARD_PHASES as readonly string[]).includes(search.phase)
      ? (search.phase as MetaCampaignType)
      : META_WIZARD_DEFAULT_PHASE;
  return {
    phase,
    offer_url: typeof search.offer_url === 'string' ? search.offer_url : undefined,
  };
}

// --- Account picker -------------------------------------------------------------------------------

/**
 * Append the return-to-origin marker to a Meta OAuth state (#5907).
 *
 * The state is opaque to Facebook and the backend strips suffixes before CSRF comparison
 * (``oauth_state.core_nonce``), so the Connect button rides the page the user connected from in a
 * ``|r<url-encoded path>`` segment. Must be applied BEFORE the local-dev ``|p<port>`` suffix — the
 * staging relay's matcher is anchored to the end of the state.
 */
export function withMetaReturnPath(state: string, pathname: string): string {
  return `${state}|r${encodeURIComponent(pathname)}`;
}

/**
 * Recover the return path from a callback's ``state`` (#5907); null when absent or unsafe.
 *
 * Only internal absolute paths (leading ``/``, not ``//``) are honoured — anything else would let a
 * crafted state turn the callback into an open redirector.
 */
export function returnPathFromMetaState(state: string | null | undefined): string | null {
  const match = (state ?? '').match(/\|r([^|]*)/);
  if (!match) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
  return decoded;
}

/**
 * True when the ad-account picker should auto-open: the integration is connected and has ad
 * accounts, but the Meta settings bundle is incomplete — no ad account selected, or one selected
 * without its Facebook Page / Meta Pixel bound (#5978). The modal runs its full 2-step flow
 * (#5977) either way, so the bundle completes account-first; it never opens once everything is
 * set, and the wizard's account-setup gate (#4652) stays as the launch-time fallback. Callers
 * gate on a once-per-session ref; this is only the data-driven half.
 */
export function shouldAutoOpenMetaAccountPicker(status: MetaIntegrationStatus | null | undefined): boolean {
  if (!status?.is_connected) return false;
  const hasAccounts = (status.ad_accounts?.length ?? 0) > 0;
  if (!hasAccounts) return false;
  const selected = status.selected_ad_account;
  if (selected == null) return true;
  return !selected.page_id || !selected.pixel_id;
}
